import React, { Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { Global404, Route404 } from '@saviynt/common';
import {
  AlertBannerProvider,
  Link as DSLink,
  OverlayProvider,
  StackingAlertBanner,
} from '@saviynt/design-system';
import cx from 'classnames';

import { AuditLogViewerContainer } from '../../pages/AuditLogViewer';
import Details from '../../pages/Details';
import EntitlementReport from '../../pages/EntitlementReport';
import InsightDashboardContainer from '../../pages/InsightDashboard';
import { LicenseIntelligence } from '../../pages/LicenseIntelligence';
import Recommendation from '../../pages/Recommendation';
import Report from '../../pages/Report';
import Violations from '../../pages/Violations';
import BannerManager from '../../shared/components/BannerManager';
import SkipToMainContentLink from '../../shared/components/SkipToMainContentLink';
import {
  AAG_ACTIVE_TABS,
  AAG_AUDIT_LOG_BASENAME,
  AAG_AUDIT_LOG_BASENAME_VIEW,
  AAG_INSIGHT_DASHBOARD_BASENAME,
  AAG_INSIGHT_DASHBOARD_VIEW,
  AAG_LICENSE_INTELLIGENCE_BASENAME,
  AAG_SOD_DETAILS_WITH_ID,
  AAG_SOD_ENT_DETAILS_WITH_ID,
  AAG_SOD_ENTITLEMENT_REPORT,
  AAG_SOD_RECOMMENDATION,
  AAG_SOD_REPORT,
  ALERT_BANNER_DEFAULT,
  BASENAME,
  invertedMessages,
} from '../../shared/constants';
import { useTypedSelector } from '../../shared/hooks';
import { globalActions } from '../../shared/store/globalSlice';
import {
  getLocalizationStats,
  hideHostAppElements,
  hideHostAppElementsCleanup,
  removeDynamicBodyClasses,
} from '../../shared/utils';

import '../../shared/styles/aagUserPreferences.css';

interface AllowedRoutes {
  staticRoutes: string[];
  dynamicRoutes: string[];
  blockedUrlList: string[];
  blockedNames: string[];
}

/**
 * Routes for AAG
 * @function
 */
function Routes() {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    user: {
      isMenuPinned,
      headerMenu: { ecmDateFormat },
      messages: ApiMessages,
    },
    aag: {
      global: { alertBanner },
    },
  } = useTypedSelector((state) => state);

  const allowedRoutes: AllowedRoutes = {
    staticRoutes: [
      ...AAG_ACTIVE_TABS,
      AAG_SOD_DETAILS_WITH_ID,
      AAG_SOD_ENT_DETAILS_WITH_ID,
      AAG_SOD_RECOMMENDATION,
      AAG_SOD_REPORT,
      AAG_SOD_ENTITLEMENT_REPORT,
      AAG_AUDIT_LOG_BASENAME,
      AAG_AUDIT_LOG_BASENAME_VIEW,
      AAG_INSIGHT_DASHBOARD_BASENAME,
      AAG_INSIGHT_DASHBOARD_VIEW,
      AAG_LICENSE_INTELLIGENCE_BASENAME,
    ],
    dynamicRoutes: [
      AAG_AUDIT_LOG_BASENAME,
      AAG_INSIGHT_DASHBOARD_BASENAME,
      AAG_LICENSE_INTELLIGENCE_BASENAME,
    ],
    blockedUrlList: [],
    blockedNames: [],
  };

  useEffect(() => {
    const isEcmDateValid = Boolean(ecmDateFormat);

    if (isEcmDateValid) {
      dispatch(globalActions.setGlobalDateFormat(ecmDateFormat));
    }

    // Localization stats for non-production version
    const isNotProduction = Boolean(process.env.NODE_ENV !== 'production');

    if (isNotProduction) {
      getLocalizationStats(ApiMessages, invertedMessages);
    }
  }, [dispatch, ecmDateFormat, ApiMessages]);

  // Personalization access checks removed; AAG routes always enabled

  useEffect(() => {
    (hideHostAppElements as () => void)();
    (removeDynamicBodyClasses as () => void)();

    return () => (hideHostAppElementsCleanup as () => void)();
  }, []);

  const alertWrapperClasses = cx(
    'Sod-AlertBannerProvider',
    isMenuPinned && 'Sod-AlertBannerProvider--isMenuPinned',
    location.pathname.includes(AAG_SOD_ENTITLEMENT_REPORT) &&
      'Sod-Ent-Report-AlertBannerProvider'
  );

  // Constants to satisfy eslint no-literals rule in JSX
  const LOADING_TEXT = 'Loading...' as const;
  const LINK_COLOR_NEUTRAL = 'neutral' as const;
  const LINK_KIND_BUTTON = 'button' as const;
  const ALERT_BANNER_TEST_ID = 'sod-alert-banner' as const;

  // Access gating removed

  // Only show the main app if access is explicitly granted
  return (
    <Suspense fallback={<div>{LOADING_TEXT}</div>}>
      <SkipToMainContentLink />
      <Router basename={BASENAME}>
        <Route404 routes={allowedRoutes} render={() => <Global404 />}>
          <Switch>
            <OverlayProvider>
              <AlertBannerProvider
                dataTheme={alertBanner.dataTheme}
                className={alertWrapperClasses}>
                <main id='aag-main-content' className='aag-root' tabIndex={-1}>
                  {/* Global BannerManager renders queued alerts across the entire AAG app */}
                  <BannerManager />
                  <Route exact path={AAG_ACTIVE_TABS} component={Violations} />
                  <Route
                    exact
                    path={AAG_SOD_DETAILS_WITH_ID}
                    component={Details}
                  />
                  <Route
                    exact
                    path={AAG_SOD_ENT_DETAILS_WITH_ID}
                    component={Details}
                  />
                  <Route
                    exact
                    path={AAG_SOD_RECOMMENDATION}
                    component={Recommendation}
                  />
                  <Route exact path={AAG_SOD_REPORT} component={Report} />
                  <Route
                    exact
                    path={AAG_SOD_ENTITLEMENT_REPORT}
                    component={EntitlementReport}
                  />
                  {/* New Audit Log Viewer Route */}
                  <Route
                    exact
                    path={AAG_AUDIT_LOG_BASENAME_VIEW}
                    component={AuditLogViewerContainer}
                  />
                  {/* Insight Dashboard Route */}
                  <Route
                    exact
                    path={AAG_INSIGHT_DASHBOARD_VIEW}
                    component={InsightDashboardContainer}
                  />
                  <Route
                    exact
                    path={AAG_LICENSE_INTELLIGENCE_BASENAME}
                    component={LicenseIntelligence}
                  />
                  <StackingAlertBanner
                    colorTheme={alertBanner.kind}
                    title={alertBanner.title}
                    description={alertBanner.description}
                    shouldAutoDismiss
                    isVisible={alertBanner.isOpen}
                    LinkComponent={
                      alertBanner.link ? (
                        <DSLink
                          text={alertBanner.link.text}
                          color={LINK_COLOR_NEUTRAL}
                          kind={LINK_KIND_BUTTON}
                          onClick={alertBanner.link.onClick}
                          suffixIconKind={alertBanner.link.suffixIcon}
                          dataTheme={alertBanner.dataTheme}
                          className={cx(
                            `Sod-AlertBannerProvider-link--is${
                              alertBanner.dataTheme as string
                            }`
                          )}
                        />
                      ) : null
                    }
                    dataTestId={ALERT_BANNER_TEST_ID}
                    onCancel={() => {
                      dispatch(
                        globalActions.setGlobalAlertBanner({
                          ...ALERT_BANNER_DEFAULT,
                          isOpen: false,
                        })
                      );
                    }}
                  />
                </main>
              </AlertBannerProvider>
            </OverlayProvider>
          </Switch>
        </Route404>
      </Router>
    </Suspense>
  );
}

export default Routes;
